/**
 * src/pricingtable/PricingTable2.jsx — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * New WordPress-aligned pricing table/grid renderer for the app.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * The legacy PricingTable.jsx still relies on older display assumptions:
 *   • raw plan-catalog features/subheaders
 *   • legacy card renderer structure
 *   • account/public fallback behavior tied to older presentation logic
 *
 * This new file is the first app renderer that consumes the same merged
 * pricing presentation payload that WordPress already drives.
 *
 * DATA SOURCES
 * ------------------------------------------------------------------
 * 1. usePricingPresentation()
 *    - loads the generated pricing-data.json payload
 *    - already includes merged plan presentation + compare data
 *
 * 2. BillingContext
 *    - current runtime subscription state
 *    - current plan / priceId / seats / status
 *
 * IMPORTANT
 * ------------------------------------------------------------------
 * Presentation payload decides:
 *   • what plans look like
 *   • feature order
 *   • tooltips
 *   • card hierarchy content
 *   • featured-plan semantics
 *
 * Billing context decides:
 *   • current subscription state
 *   • which plan is current
 *   • which button actions are allowed
 *
 * This file keeps those responsibilities separate.
 *
 * CURRENT SCOPE
 * ------------------------------------------------------------------
 * For this step, this file:
 *   • loads the payload
 *   • filters visible plans
 *   • filters by pricing interval
 *   • passes the merged plan object to PlanCard2
 *   • logs key diagnostics for local testing
 *
 * It intentionally does NOT render the compare table yet.
 */

import React, { useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Typography, CircularProgress } from "@mui/material";

import { useBillings } from "../contexts/BillingContext";
import PlanCard2 from "./PlanCard2";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

/**
 * Convert plan pricing for display at the selected interval.
 *
 * WordPress behavior:
 * - annual plans display monthly-equivalent per-seat pricing
 * - monthly plans display monthly per-seat pricing directly
 */
function getDisplayedSeatPrice(interval, perSeat) {
  if (perSeat == null) return null;

  const numeric = Number(perSeat);
  if (!Number.isFinite(numeric)) return null;

  if (interval === "annual") {
    return numeric / 12;
  }

  return numeric;
}

/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */
const PricingTable2 = ({
  context,
  onPlanSelect,
  seatCount,
  onSeatCountChange,
  pricingInterval,
  onIntervalChange,
  pricingPresentation,
}) => {
  const { currentSubscription } = useBillings() || {};

  const effectiveInterval =
    pricingInterval === "monthly" || pricingInterval === "annual"
      ? pricingInterval
      : (pricingPresentation?.defaults?.interval === "monthly" ? "monthly" : "annual");

  /* ----------------------------------------------------------------
   * Diagnostics
   * ---------------------------------------------------------------- */
  useEffect(() => {
    console.log("[PricingTable2] Mounted:", {
      context,
      pricingInterval,
      effectiveInterval,
      seatCount,
      hasCurrentSubscription: !!currentSubscription,
    });
  }, []);

  useEffect(() => {
    console.log("[PricingTable2] Payload state:", {
      loading: pricingPresentation?.loading,
      error: pricingPresentation?.error || null,
      payloadPath: pricingPresentation?.payloadPath || null,
      target: pricingPresentation?.target || null,
      pricingEnv: pricingPresentation?.pricingEnv || null,
      planKeys: Array.isArray(pricingPresentation?.plans)
        ? pricingPresentation.plans.map((plan) => plan.key)
        : [],
      compareCategories: Array.isArray(pricingPresentation?.compare)
        ? pricingPresentation.compare.map((group) => group.category)
        : [],
    });
  }, [
    pricingPresentation?.loading,
    pricingPresentation?.error,
    pricingPresentation?.payloadPath,
    pricingPresentation?.target,
    pricingPresentation?.pricingEnv,
    pricingPresentation?.plans,
    pricingPresentation?.compare,
  ]);

  useEffect(() => {
    console.log("[PricingTable2] Render state update:", {
      context,
      effectiveInterval,
      seatCount,
      currentSubscription,
      topSpacingRemoved: true,
      gridMarginTop: 0,
    });
  }, [context, effectiveInterval, seatCount, currentSubscription]);

  /* ----------------------------------------------------------------
   * Visible plans from merged payload
   *
   * We only render base plans in the current app pricing grid:
   *   • standard
   *   • pro
   *   • enterprise
   *
   * Add-ons are intentionally excluded from this UI for now.
   *
   * ENTERPRISE ALIGNMENT RULE
   * ----------------------------------------------------------------
   * PricingTable2 must hand downstream components a plan object that is
   * already stable for the ACTIVE billing interval.
   *
   * Why:
   * - SubscriptionPage2 stores the selected plan object in reviewContext.
   * - ReviewChangesDrawer2 expects that selected plan object to already
   *   contain the billing fields needed for preview / checkout orchestration:
   *     • key
   *     • name
   *     • interval
   *     • priceId
   *     • flatFee
   *     • perSeat
   *     • pricingDisplay
   * - We should not depend on PlanCard2 to silently "fill in" billing
   *   fields later for runtime correctness.
   *
   * This keeps the ownership boundary clean:
   * - PricingTable2 prepares the active-interval plan object
   * - PlanCard2 renders it
   * - SubscriptionPage2 stores it
   * - ReviewChangesDrawer2 consumes it
   * ---------------------------------------------------------------- */
  const visiblePlans = useMemo(() => {
    const rawPlans = Array.isArray(pricingPresentation?.plans)
      ? pricingPresentation.plans
      : [];

    const next = rawPlans
      .filter((plan) => plan?.kind === "base")
      .map((plan) => {
        const activePricing =
          plan?.pricing?.[effectiveInterval] || {};

        const displayedSeatPrice = getDisplayedSeatPrice(
          effectiveInterval,
          activePricing?.perSeat ?? null,
        );

        const normalizedPlan = {
          ...plan,

          /* --------------------------------------------------------
           * Active interval metadata
           * ------------------------------------------------------ */
          interval: effectiveInterval,
          displayedSeatPrice,

          /* --------------------------------------------------------
           * Active interval billing fields
           *
           * IMPORTANT:
           * - These fields are consumed later by:
           *     • SubscriptionPage2 reviewContext
           *     • ReviewChangesDrawer2
           * - Keep them explicit here so downstream billing logic does not
           *   depend on PlanCard2 to reconstruct them.
           * ------------------------------------------------------ */
          priceId: activePricing?.priceId || null,
          flatFee:
            Number.isFinite(Number(activePricing?.flatFee)) ?
              Number(activePricing.flatFee) :
              0,
          perSeat:
            Number.isFinite(Number(activePricing?.perSeat)) ?
              Number(activePricing.perSeat) :
              null,

          /* --------------------------------------------------------
           * Preserve full interval pricing for card display / future UI
           * ------------------------------------------------------ */
          monthlyPricing:
            plan?.pricing?.monthly ?? {flatFee: 0, perSeat: null},
          annualPricing:
            plan?.pricing?.annual ?? {flatFee: 0, perSeat: null},
        };

        return normalizedPlan;
      });

    console.log("[PricingTable2] Computed visiblePlans:", {
      effectiveInterval,
      planKeys: next.map((plan) => plan.key),
      displayedSeatPrices: next.map((plan) => ({
        key: plan.key,
        displayedSeatPrice: plan.displayedSeatPrice,
        pricingDisplay: plan.pricingDisplay,
        priceId: plan.priceId,
        flatFee: plan.flatFee,
        perSeat: plan.perSeat,
        interval: plan.interval,
      })),
    });

    return next;
  }, [pricingPresentation?.plans, effectiveInterval]);
  /**
   * Keep callback shape aligned with the existing page shell.
   */
  const handlePlanSelect = useCallback((plan) => {
    console.log("[PricingTable2] handlePlanSelect:", {
      key: plan?.key || null,
      name: plan?.name || null,
      interval: plan?.interval || null,
      pricingDisplay: plan?.pricingDisplay || null,
    });

    if (typeof onPlanSelect === "function") {
      onPlanSelect(plan);
    }
  }, [onPlanSelect]);

  /* ----------------------------------------------------------------
   * Loading state
   * ---------------------------------------------------------------- */
  if (pricingPresentation?.loading) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 5,
        }}
      >
        <CircularProgress size={30} />
        <Typography
          variant="body2"
          color="text.secondary"
          mt={2}
          sx={{ fontFamily: "Roboto, sans-serif" }}
        >
          Loading pricing plans...
        </Typography>
      </Box>
    );
  }

  /* ----------------------------------------------------------------
   * Error state
   * ---------------------------------------------------------------- */
  if (pricingPresentation?.error) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 5,
        }}
      >
        <Typography
          variant="h6"
          color="error"
          sx={{ fontFamily: "Roboto, sans-serif", mb: 1 }}
        >
          Unable to load pricing plans
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: "Roboto, sans-serif" }}
        >
          {pricingPresentation.error}
        </Typography>
      </Box>
    );
  }

  /* ----------------------------------------------------------------
   * Empty state
   * ---------------------------------------------------------------- */
  if (!visiblePlans.length) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 5,
        }}
      >
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontFamily: "Roboto, sans-serif" }}
        >
          No plans available for the selected interval.
        </Typography>
      </Box>
    );
  }

  /* ----------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  return (
    <Box
      sx={{
        mt: 0,
      }}
    >
      {/* ------------------------------------------------------------
       * Card grid
       * Mirrors the structural role of .utm-pricing-grid in WordPress.
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(3, minmax(0, 1fr))",
          },
          gap: "24px",
          alignItems: "stretch",
          mt: 0,
        }}
      >
        {visiblePlans.map((plan) => (
          <PlanCard2
            key={`${plan.key}-${effectiveInterval}`}
            plan={plan}
            seatCount={seatCount}
            context={context}
            interval={effectiveInterval}
            currentSubscription={currentSubscription}
            onPlanSelect={handlePlanSelect}
          />
        ))}
      </Box>
    </Box>
  );
};

PricingTable2.propTypes = {
  context: PropTypes.oneOf(["public", "account"]).isRequired,
  onPlanSelect: PropTypes.func.isRequired,
  seatCount: PropTypes.number.isRequired,
  onSeatCountChange: PropTypes.func,
  pricingInterval: PropTypes.oneOf(["monthly", "annual"]),
  onIntervalChange: PropTypes.func,
  pricingPresentation: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.string,
    payloadPath: PropTypes.string,
    target: PropTypes.string,
    pricingEnv: PropTypes.string,
    defaults: PropTypes.shape({
      interval: PropTypes.string,
    }),
    plans: PropTypes.array,
    compare: PropTypes.array,
  }),
};

export default React.memo(PricingTable2);